import axios from 'axios';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const SliderManager = () => {
  const [sliders, setSliders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    order: 1,
    active: true
  });

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    try {
      const response = await axios.get(`${API}/sliders`);
      setSliders(response.data);
    } catch (error) {
      console.error('Error fetching sliders:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      const response = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSlider) {
        await axios.put(`${API}/sliders/${editingSlider.slide_id}`, formData);
        toast.success('Slider updated successfully');
      } else {
        await axios.post(`${API}/sliders`, formData);
        toast.success('Slider created successfully');
      }
      setShowForm(false);
      setEditingSlider(null);
      setFormData({ title: '', subtitle: '', image_url: '', order: 1, active: true });
      fetchSliders();
    } catch (error) {
      toast.error('Failed to save slider');
    }
  };

  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setFormData({
      title: slider.title,
      subtitle: slider.subtitle,
      image_url: slider.image_url,
      order: slider.order,
      active: slider.active
    });
    setShowForm(true);
  };

  const handleDelete = async (slide_id) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await axios.delete(`${API}/sliders/${slide_id}`);
        toast.success('Slider deleted');
        fetchSliders();
      } catch (error) {
        toast.error('Failed to delete slider');
      }
    }
  };

  return (
    <div data-testid="slider-manager">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-3xl font-medium text-white">Hero Slider Management</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingSlider(null);
            setFormData({ title: '', subtitle: '', image_url: '', order: 1, active: true });
          }}
          data-testid="add-slider-button"
          className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2"
        >
          <Plus size={16} />
          Add Slide
        </button>
      </div>

      {showForm && (
        <div className="bg-slate-900 border border-white/10 p-8 mb-8" data-testid="slider-form">
          <h2 className="text-xl font-semibold text-white mb-6">
            {editingSlider ? 'Edit Slide' : 'Add New Slide'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Slide Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="slider-input-title"
                className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
              />
              <input
                type="text"
                placeholder="Slide Subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                required
                data-testid="slider-input-subtitle"
                className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Upload Image</label>
              <div className="flex gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="flex-1 bg-slate-900/50 border border-white/10 focus:border-ocean-500 p-3 text-white outline-none"
                />
                {uploading && <span className="text-ocean-500">Uploading...</span>}
              </div>
              {formData.image_url && (
                <div className="mt-2">
                  <img src={formData.image_url} alt="Preview" className="h-32 object-cover border border-white/10" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                placeholder="Order"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                required
                min="1"
                data-testid="slider-input-order"
                className="bg-slate-900/50 border border-white/10 focus:border-ocean-500 h-12 px-4 text-white placeholder:text-slate-500 outline-none"
              />
              <label className="flex items-center gap-3 text-white">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5"
                />
                Active
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                data-testid="slider-submit-button"
                className="px-6 py-3 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all"
              >
                {editingSlider ? 'Update' : 'Create'} Slide
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSlider(null);
                }}
                className="px-6 py-3 border border-white/20 text-slate-400 hover:text-white hover:border-white font-medium uppercase tracking-widest text-xs transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {sliders.map((slider) => (
          <div
            key={slider.slide_id}
            data-testid={`slider-item-${slider.slide_id}`}
            className="bg-slate-900 border border-white/10 p-6 flex items-center gap-6"
          >
            <img src={slider.image_url} alt={slider.title} className="w-32 h-20 object-cover border border-white/10" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">{slider.title}</h3>
              <p className="text-sm text-slate-400 mb-2">{slider.subtitle}</p>
              <div className="flex gap-3 text-xs">
                <span className="text-slate-500">Order: {slider.order}</span>
                <span className={slider.active ? 'text-green-500' : 'text-red-500'}>
                  {slider.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(slider)}
                className="p-2 border border-white/10 hover:border-ocean-500 text-slate-400 hover:text-white transition-colors"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => handleDelete(slider.slide_id)}
                className="p-2 border border-white/10 hover:border-red-500 text-slate-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SliderManager;
